import React, { useEffect, useState } from 'react';
import { fetchMediaBlob } from '../../../lib/api';

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document', 'sticker']);

export const isMediaMessage = (m) => {
  const type = String(m?.message_type || m?.type || '').toLowerCase();
  return MEDIA_TYPES.has(type) && !!(m?.media_id);
};

export const mediaLabel = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'image') return 'Image';
  if (t === 'video') return 'Video';
  if (t === 'audio') return 'Audio';
  if (t === 'sticker') return 'Sticker';
  if (t === 'document') return 'Document';
  return 'Media';
};

const resolveKind = (messageType, mimeType) => {
  const type = String(messageType || '').toLowerCase();
  const mime = String(mimeType || '').toLowerCase();
  if (type === 'image' || type === 'sticker' || mime.startsWith('image/')) return 'image';
  if (type === 'video' || mime.startsWith('video/')) return 'video';
  if (type === 'audio' || mime.startsWith('audio/')) return 'audio';
  if (type === 'document') return 'document';
  return 'file';
};

export const MediaMessage = ({
  mediaId,
  messageType,
  mimeType,
  caption,
  filename,
  compact = false,
}) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const kind = resolveKind(messageType, mimeType);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const load = async () => {
      if (!mediaId) {
        setError('No media');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      setUrl('');
      try {
        const blob = await fetchMediaBlob(mediaId);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(
            status === 410
              ? 'Media expired'
              : 'Unable to load media'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);

  if (loading) {
    return (
      <div className={`chat-media chat-media--loading ${compact ? 'compact' : ''}`}>
        Loading media…
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className={`chat-media chat-media--error ${compact ? 'compact' : ''}`}>
        [{mediaLabel(messageType)}] {error || 'Unavailable'}
        {caption ? <div className="chat-media-caption">{caption}</div> : null}
      </div>
    );
  }

  return (
    <div className={`chat-media ${compact ? 'compact' : ''}`}>
      {kind === 'image' && (
        <a href={url} target="_blank" rel="noreferrer" className="chat-media-link">
          <img src={url} alt={caption || filename || 'Image'} className="chat-media-img" />
        </a>
      )}
      {kind === 'video' && <video src={url} controls className="chat-media-video" />}
      {kind === 'audio' && <audio src={url} controls className="chat-media-audio" />}
      {(kind === 'document' || kind === 'file') && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          download={filename || undefined}
          className="chat-media-doc"
        >
          {filename || 'Open media'}
        </a>
      )}
      {caption ? <div className="chat-media-caption">{caption}</div> : null}
    </div>
  );
};

export default MediaMessage;
